/**
 * AI.GEN lines=1-100
 * 全局设置 Store - 管理订单业务类型、缩放值等全局设置
 */
import { create } from 'zustand';
import { useStore } from 'zustand/react';

import { OrderBusinessType } from '@/common/enum';

/**
 * HP订单子选项类型
 */
export type HPOrderSubOption =
  | 'onlyPandaDeliveryOrders'
  | 'farawayOrderOnly'
  | 'showSpecialDriverOrders'
  | 'groupMeal';

/**
 * 全局设置状态
 */
interface SettingsState {
  /** 订单业务类型数组 */
  orderBusinessType: OrderBusinessType[];
  /** HP订单子选项 */
  checkedList: HPOrderSubOption[];
  /** PF订单配送标签列表 */
  deliveryTagList: string[];
  /** 缩放值 */
  zoom: number;
  /** 时间类型（0: 下单时间, 1: 送达时间） */
  timeType: 0 | 1;
  /** 是否隐藏伪上线骑手 */
  pseudoOnline: boolean;
  /** 是否默认显示取送点位置 */
  iconDefaultCheck: boolean;
  /** 是否填写撤销理由 */
  isFillReason: boolean;
}

/**
 * 全局设置操作
 */
interface SettingsActions {
  /** 设置订单业务类型 */
  setOrderBusinessType: (types: OrderBusinessType[]) => void;
  /** 设置HP订单子选项 */
  setCheckedList: (list: HPOrderSubOption[]) => void;
  /** 设置PF订单配送标签列表 */
  setDeliveryTagList: (list: string[]) => void;
  /** 设置缩放值 */
  setZoom: (zoom: number) => void;
  /** 获取缩放值 */
  getZoom: () => number;
  /** 设置时间类型 */
  setTimeType: (type: 0 | 1) => void;
  /** 设置是否隐藏伪上线骑手 */
  setPseudoOnline: (value: boolean) => void;
  /** 设置是否默认显示取送点位置 */
  setIconDefaultCheck: (value: boolean) => void;
  /** 设置是否填写撤销理由 */
  setIsFillReason: (value: boolean) => void;
}

type SettingsStore = SettingsState & SettingsActions;

// 从 localStorage 读取初始值
const getInitialZoom = (): number => {
  const value = localStorage.getItem('dispatchZoom');
  if (!value) {
    return 100;
  }
  try {
    return JSON.parse(value) || 100;
  } catch {
    return 100;
  }
};

const getInitialOrderBusinessType = (): OrderBusinessType[] => {
  const value = localStorage.getItem('orderBusinessType');
  if (!value) {
    return [];
  }
  try {
    return value.split(',').map((v) => Number(v) as OrderBusinessType);
  } catch {
    return [];
  }
};

const getInitialDeliveryTagList = (): string[] => {
  const value = localStorage.getItem('deliveryTagList');
  if (!value) {
    return [];
  }
  try {
    return value.split(',');
  } catch {
    return [];
  }
};

const getInitialIsFillReason = (): boolean => {
  return sessionStorage.getItem('isFillReason') === 'true';
};

const getInitialIconDefaultCheck = (): boolean => {
  return sessionStorage.getItem('iconDefaultCheck') === 'true';
};

const initialState: SettingsState = {
  orderBusinessType: getInitialOrderBusinessType(),
  checkedList: [
    'onlyPandaDeliveryOrders',
    'groupMeal',
    'showSpecialDriverOrders',
    'farawayOrderOnly',
  ],
  deliveryTagList: getInitialDeliveryTagList(),
  zoom: getInitialZoom(),
  timeType: 1,
  pseudoOnline: false,
  iconDefaultCheck: getInitialIconDefaultCheck(),
  isFillReason: getInitialIsFillReason(),
};

export const SettingsStore = create<SettingsStore>((set, get) => ({
  ...initialState,

  setOrderBusinessType: (types) => {
    set({ orderBusinessType: types });
    localStorage.setItem('orderBusinessType', types.join(','));
  },

  setCheckedList: (list) => {
    set({ checkedList: list });
  },

  setDeliveryTagList: (list) => {
    set({ deliveryTagList: list });
    localStorage.setItem('deliveryTagList', list.join(','));
  },

  setZoom: (zoom) => {
    localStorage.setItem('dispatchZoom', JSON.stringify(zoom));
    set({ zoom });
  },

  getZoom: () => {
    return get().zoom;
  },

  setTimeType: (type) => {
    set({ timeType: type });
  },

  setPseudoOnline: (value) => {
    set({ pseudoOnline: value });
  },

  setIconDefaultCheck: (value) => {
    set({ iconDefaultCheck: value });
    sessionStorage.setItem('iconDefaultCheck', String(value));
  },

  setIsFillReason: (value) => {
    set({ isFillReason: value });
    sessionStorage.setItem('isFillReason', String(value));
  },
}));

// 重载签名
export function useSettingsStore(): SettingsStore;
export function useSettingsStore<T>(selector: (state: SettingsStore) => T): T;

// 实现签名
export function useSettingsStore<T>(
  selector?: (state: SettingsStore) => T,
): T | SettingsStore {
  if (!selector) {
    return useStore(SettingsStore);
  }
  return useStore(SettingsStore, selector);
}
